import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, LogIn } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_jax-craft-remodel/artifacts/6r3h6sd1_Transparent%20Logo.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Projects', path: '/projects' },
  { name: 'Process', path: '/process' },
  { name: 'About', path: '/about' },
  { name: 'Service Areas', path: '/service-areas' },
  { name: 'Reviews', path: '/reviews' },
  { name: 'FAQ', path: '/faq' },
  { name: 'Contact', path: '/contact' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link to="/" className="relative z-50" data-testid="logo-link">
              <img
                src={LOGO_URL}
                alt="Jax Remodeling Solutions"
                className="h-14 md:h-16 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`text-xs font-semibold uppercase tracking-widest transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-[#C6A87C]'
                      : 'text-[#0A0A0A] hover:text-[#C6A87C]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:flex items-center gap-6">
              <a
                href="tel:9048636673"
                data-testid="header-phone-btn"
                className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0A0A0A] hover:text-[#C6A87C] transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span>(904) 863-6673</span>
              </a>
              <Link
                to="/portal/login"
                data-testid="header-portal-login-btn"
                className="flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-xs font-semibold uppercase tracking-widest hover:bg-[#C6A87C] transition-colors"
              >
                <LogIn className="w-4 h-4" />
                <span>Portal</span>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden relative z-50 p-2"
              data-testid="mobile-menu-toggle"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[#0A0A0A]" />
              ) : (
                <Menu className="w-6 h-6 text-[#0A0A0A]" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
            data-testid="mobile-menu"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/20"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="absolute right-0 top-0 bottom-0 w-[80%] max-w-sm bg-white shadow-2xl"
            >
              <div className="pt-24 px-8 pb-8 h-full overflow-y-auto">
                <nav className="flex flex-col gap-6">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={link.path}
                        data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`block text-lg font-medium ${
                          location.pathname === link.path
                            ? 'text-[#C6A87C]'
                            : 'text-[#0A0A0A]'
                        }`}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </nav>

                {/* Mobile CTA */}
                <div className="mt-12 pt-8 border-t border-gray-100 space-y-3">
                  <a
                    href="tel:9048636673"
                    data-testid="mobile-phone-btn"
                    className="flex items-center justify-center gap-3 w-full py-4 bg-[#0A0A0A] text-white text-sm font-semibold uppercase tracking-widest"
                  >
                    <Phone className="w-4 h-4" />
                    <span>Call Now</span>
                  </a>
                  <Link
                    to="/portal/login"
                    data-testid="mobile-portal-login-btn"
                    className="flex items-center justify-center gap-3 w-full py-4 border-2 border-[#C6A87C] text-[#C6A87C] text-sm font-semibold uppercase tracking-widest hover:bg-[#C6A87C] hover:text-white transition-colors"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Portal Login</span>
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
